import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, OnInit, Output, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, take } from 'rxjs/operators';
import { TypedLookupHit, EntityLookupService } from './entity-lookup.service';
import { DmsLinkEntityType, EntityLookupItem } from './entity-lookup.types';
import {
  CHILD_TYPE_OF,
  EntityTreePickerSelection,
  PickerTab,
  PROJEKTBAUM_TYPES,
  TreeNode,
  entityTypeLabel,
} from './entity-tree-picker.types';
import { BestandsknotenService } from '../../services/bestandsknoten.service';
import { BestandsknotenResponse } from '../../models/bestandsknoten.models';

interface BkPickerNode {
  data: BestandsknotenResponse;
  expanded: boolean;
  loading: boolean;
  error: boolean;
  children: BkPickerNode[] | null;
}

const BK_BLATT_TYPES = ['raum', 'raster'];

@Component({
  selector: 't2d-entity-tree-picker',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './t2d-entity-tree-picker.component.html',
  styleUrls: ['./t2d-entity-tree-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class T2dEntityTreePickerComponent implements OnInit {
  @Output() readonly selected = new EventEmitter<EntityTreePickerSelection>();
  @Output() readonly closed = new EventEmitter<void>();

  protected readonly lookup = inject(EntityLookupService);
  private readonly bestandsknotenService = inject(BestandsknotenService);

  readonly activeTab = signal<PickerTab>('projektbaum');
  readonly roots = signal<TreeNode[]>([]);
  readonly rootsLoading = signal<boolean>(false);
  readonly rootsError = signal<boolean>(false);

  readonly bkRoots = signal<BkPickerNode[]>([]);
  readonly bkRootsLoading = signal<boolean>(false);
  readonly bkRootsError = signal<boolean>(false);

  // Projekt-Baum-Cross-Typ-Suche
  readonly searchActive = signal<boolean>(false);
  readonly searchHits = signal<readonly TypedLookupHit[]>([]);

  // Org/AP-Flachlisten-Suche
  readonly flatResults = signal<readonly EntityLookupItem[]>([]);
  readonly flatLoading = signal<boolean>(false);

  private readonly projektbaumQuery$ = new Subject<string>();
  private readonly flatQuery$ = new Subject<string>();

  constructor() {
    // Projekt-Baum reactive search pipeline: debounce + cancel stale via switchMap
    this.projektbaumQuery$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => {
          if (term === '') {
            this.searchActive.set(false);
            this.searchHits.set([]);
            return [];
          }
          this.searchActive.set(true);
          return this.lookup.searchAcross(PROJEKTBAUM_TYPES, term, 20);
        }),
        takeUntilDestroyed(),
      )
      .subscribe((hits) => {
        this.searchHits.set(hits);
      });

    // Org/AP flat search pipeline: debounce + cancel stale via switchMap
    this.flatQuery$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => {
          const type = this.flatTypeForTab();
          return this.lookup.search(type, term === '' ? null : term, 50);
        }),
        takeUntilDestroyed(),
      )
      .subscribe((items) => {
        this.flatResults.set(items);
        this.flatLoading.set(false); // Tipp-Ergebnisse beenden den initialen Ladezustand
      });
  }

  ngOnInit(): void {
    this.loadRoots();
  }

  private loadRoots(): void {
    this.rootsLoading.set(true);
    this.rootsError.set(false);
    this.lookup
      .search('projekt', null, 50)
      .pipe(take(1))
      .subscribe({
        next: (items) => {
          this.roots.set(items.map((item) => this.makeNode('projekt', item)));
          this.rootsLoading.set(false);
        },
        error: () => {
          this.rootsError.set(true);
          this.rootsLoading.set(false);
        },
      });
  }

  retryRoots(): void {
    if (this.rootsLoading()) return;
    this.loadRoots();
  }

  private makeNode(type: DmsLinkEntityType, item: EntityLookupItem): TreeNode {
    return { type, item, expanded: false, loading: false, error: false, children: null };
  }

  hasChildren(type: DmsLinkEntityType): boolean {
    return (CHILD_TYPE_OF[type] ?? null) !== null;
  }

  toggleExpand(node: TreeNode): void {
    if (!this.hasChildren(node.type)) {
      return;
    }
    node.expanded = !node.expanded;
    if (node.expanded && node.children === null && !node.loading) {
      this.loadChildren(node);
    }
  }

  private loadChildren(node: TreeNode): void {
    const childType = CHILD_TYPE_OF[node.type] ?? null;
    if (childType === null) {
      return;
    }
    node.loading = true;
    node.error = false;
    this.lookup
      .listChildren(node.type, node.item.id, childType)
      .pipe(take(1))
      .subscribe({
        next: (items) => {
          node.children = items.map((item) => this.makeNode(childType, item));
          node.loading = false;
          this.roots.set([...this.roots()]); // OnPush: neue Array-Referenz triggert CD
        },
        error: () => {
          node.error = true;
          node.loading = false;
          this.roots.set([...this.roots()]);
        },
      });
  }

  retryNode(node: TreeNode): void {
    node.children = null;
    this.loadChildren(node);
  }

  pick(node: TreeNode): void {
    this.emitSelection(node.type, node.item);
  }

  selectTab(tab: PickerTab): void {
    this.activeTab.set(tab);
    this.searchActive.set(false);
    this.searchHits.set([]);
    this.flatResults.set([]);
    if (tab === 'organisation' || tab === 'ansprechpartner') {
      this.loadFlat();
    } else if (tab === 'bestandsknoten') {
      if (this.bkRoots().length === 0) {
        this.loadBkRoots();
      }
    }
  }

  /** Lädt die erste (ungefilterte) Flachliste für den aktiven Org/AP-Tab. */
  private loadFlat(): void {
    const tab = this.activeTab();
    const type = this.flatTypeForTab();
    this.flatLoading.set(true);
    this.lookup
      .search(type, null, 50)
      .pipe(take(1))
      .subscribe({
        next: (items) => {
          if (this.activeTab() !== tab) return; // Tab inzwischen gewechselt → verwerfen
          this.flatResults.set(items);
          this.flatLoading.set(false);
        },
        error: () => {
          if (this.activeTab() !== tab) return;
          this.flatLoading.set(false);
        },
      });
  }

  /** Feeds the Projekt-Baum search subject (called from (input) event). */
  onProjektbaumQuery(term: string): void {
    this.projektbaumQuery$.next(term.trim());
  }

  private flatTypeForTab(): DmsLinkEntityType {
    return this.activeTab() === 'organisation' ? 'organisation' : 'ansprechpartner';
  }

  /** Feeds the flat (Org/AP) search subject (called from (input) event). */
  onFlatQuery(term: string): void {
    this.flatQuery$.next(term.trim());
  }

  /**
   * Public API retained for backward-compat and direct testing.
   * Pushes the term into the debounced pipeline.
   */
  runProjektbaumSearch(q: string): void {
    this.projektbaumQuery$.next(q.trim());
  }

  /**
   * Public API retained for backward-compat and direct testing.
   * Pushes the term into the debounced pipeline.
   */
  runFlatSearch(q: string): void {
    this.flatQuery$.next(q.trim());
  }

  pickHit(hit: TypedLookupHit): void {
    this.emitSelection(hit.type, hit.item);
  }

  pickFlat(type: DmsLinkEntityType, item: EntityLookupItem): void {
    this.emitSelection(type, item);
  }

  private emitSelection(type: DmsLinkEntityType, item: EntityLookupItem): void {
    this.selected.emit({
      entityType: type,
      entityId: item.id,
      label: item.label,
      parentIds: item.parentIds ?? undefined,
      lph: item.lph ?? undefined,
    });
    // Overlay bleibt offen (Spec §5: Mehrfachauswahl nacheinander).
  }

  entityTypeLabel(type: DmsLinkEntityType | null | undefined): string {
    return entityTypeLabel(type);
  }

  /**
   * Kürzt das vom Backend gelieferte Pfad-Label (z.B.
   * "Projekt / Vertrag / §34 / LPH 1 / a) Klären …") auf das letzte Segment,
   * damit im Baum nur der Name der jeweiligen Ebene erscheint — der Pfad ist
   * über die Baum-Position bereits sichtbar. Separator ist " / " (mit Spaces);
   * Codes wie "21-0502/3" enthalten kein " / " und bleiben unangetastet.
   * Der volle Pfad bleibt als title-Tooltip am Element erhalten.
   */
  shortLabel(label: string): string {
    const parts = label.split(' / ');
    return parts[parts.length - 1] || label;
  }

  // --- Bestandsknoten-Baum ---

  private makeBkNode(data: BestandsknotenResponse): BkPickerNode {
    return { data, expanded: false, loading: false, error: false, children: null };
  }

  bkHasChildren(typ: string): boolean {
    return !BK_BLATT_TYPES.includes(typ);
  }

  private loadBkRoots(): void {
    this.bkRootsLoading.set(true);
    this.bkRootsError.set(false);
    this.bestandsknotenService
      .listChildren(null)
      .pipe(take(1))
      .subscribe({
        next: (items) => {
          this.bkRoots.set(items.map((i) => this.makeBkNode(i)));
          this.bkRootsLoading.set(false);
        },
        error: () => {
          this.bkRootsError.set(true);
          this.bkRootsLoading.set(false);
        },
      });
  }

  retryBkRoots(): void {
    if (this.bkRootsLoading()) return;
    this.loadBkRoots();
  }

  bkToggleExpand(node: BkPickerNode): void {
    if (!this.bkHasChildren(node.data.typ)) return;
    node.expanded = !node.expanded;
    if (node.expanded && node.children === null && !node.loading) {
      this.loadBkChildren(node);
    } else {
      this.bkRoots.set([...this.bkRoots()]);
    }
  }

  private loadBkChildren(node: BkPickerNode): void {
    node.loading = true;
    node.error = false;
    this.bkRoots.set([...this.bkRoots()]);
    this.bestandsknotenService
      .listChildren(node.data.id)
      .pipe(take(1))
      .subscribe({
        next: (items) => {
          node.children = items.map((i) => this.makeBkNode(i));
          node.loading = false;
          this.bkRoots.set([...this.bkRoots()]);
        },
        error: () => {
          node.error = true;
          node.loading = false;
          this.bkRoots.set([...this.bkRoots()]);
        },
      });
  }

  retryBkNode(node: BkPickerNode): void {
    if (node.loading) return;
    node.children = null;
    this.loadBkChildren(node);
  }

  private readonly bkLabelMap: Record<string, string> = {
    liegenschaft: 'Liegenschaft',
    gebaeude: 'Gebäude',
    bauteil: 'Bauteil',
    ebene: 'Ebene',
    raum: 'Raum',
    raster: 'Raster',
  };

  bkTypLabel(typ: string): string {
    return this.bkLabelMap[typ] ?? typ;
  }

  pickBestandsknoten(node: BkPickerNode): void {
    this.selected.emit({
      entityType: 'bestandsknoten',
      entityId: node.data.id,
      label: node.data.pfad.join(' / '),
    });
  }

  close(): void {
    this.closed.emit();
  }

  /** Schließt das Overlay nur, wenn auf den Backdrop selbst (nicht das Panel) geklickt wird. */
  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.close();
    }
  }
}
